<template>
  <div class="workorder-table-container">
    <div class="flex-container">
      <span class="title-mark"></span>
      <div class="name">工单列表</div>
      <input
        class="search-input"
        v-model="searchText"
        placeholder="搜索工单号/客户/设备"
        type="text"
        @input="onSearchInput"
        @keyup.enter="handleEnter"
      />
      <select class="status-select" v-model="statusFilter">
        <option value="">全部状态</option>
        <option
          v-for="opt in statusOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
    </div>
    <el-table
      class="el-table-overview"
      v-loading="loading"
      element-loading-background="rgba(2, 11, 26, 0.6)"
      :data="displayList"
      @row-click="rowClick"
      :header-cell-style="headerCellStyle"
      :cell-style="tableCellStyle"
      :row-class-name="rowClassName"
      height="100%"
    >
      <el-table-column
        v-for="(item, index) in tableLabelList"
        :prop="item.prop"
        :label="item.label"
        :key="index"
        :min-width="item.minWidth"
        :width="item.width"
        :sortable="item.sortable"
        :show-overflow-tooltip="item.prop !== 'customerName'"
        :class-name="item.prop === 'customerName' ? 'col-wrap-text' : ''"
      >
        <template slot-scope="scope">
          <span v-if="item.prop === 'workOrderStatus'">
            <el-tag :style="statusTagStyle(scope.row.workOrderStatus)" size="small">
              {{ statusLabel(scope.row.workOrderStatus) }}
            </el-tag>
          </span>
          <span v-else-if="item.prop === 'urgencyLevel'">
            {{ urgencyLabel(scope.row.urgencyLevel) }}
          </span>
          <span v-else-if="item.prop === 'progressNode'">
            {{ progressLabel(scope.row.progressNode) }}
          </span>
          <span v-else-if="item.prop === 'customerName'" class="wrap-text-cell">
            {{ scope.row.customerName || "-" }}
          </span>
          <span v-else>{{ scope.row[item.prop] || "-" }}</span>
        </template>
      </el-table-column>
    </el-table>
    <i class="corner-bl"></i>
    <i class="corner-br"></i>
  </div>
</template>

<script>
import {
  WORK_ORDER_STATUS,
  statusLabel,
  urgencyLabel,
  progressLabel,
  statusTagStyle,
} from "@/common/js/workOrderDict";

export default {
  name: "workOrderTable",
  props: {
    tableData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchText: "",
      searchTimer: null,
      statusFilter: "",
      statusOptions: Object.keys(WORK_ORDER_STATUS).map((key) => ({
        value: key,
        label: WORK_ORDER_STATUS[key],
      })),
      tableLabelList: [
        { prop: "deviceName", label: "设备名称", minWidth: 220 },
        { prop: "customerName", label: "客户", minWidth: 140 },
        { prop: "urgencyLevel", label: "紧急度", minWidth: 64 },
        { prop: "workOrderStatus", label: "状态", minWidth: 90, sortable: true },
        { prop: "progressNode", label: "进度", minWidth: 72 },
        { prop: "aftersaleName", label: "售后人员", minWidth: 76 },
        { prop: "submitTime", label: "提单时间", minWidth: 140, sortable: true },
      ],
      headerCellStyle: {
        background: "rgba(0, 100, 140, 0.75)",
        color: "#ffffff",
        fontWeight: "600",
        fontSize: "12px",
        textAlign: "center",
        padding: "7px 0",
        borderBottom: "1px solid rgba(0, 229, 255, 0.45)",
        letterSpacing: "1px",
      },
    };
  },
  computed: {
    displayList() {
      let list = Array.isArray(this.tableData) ? this.tableData : [];
      if (this.statusFilter) {
        list = list.filter((item) => item.workOrderStatus === this.statusFilter);
      }
      const kw = String(this.searchText || "").trim().toLowerCase();
      if (!kw) return list;
      return list.filter((item) => {
        const id = String(item.workOrderId || "").toLowerCase();
        const customer = String(item.customerName || "").toLowerCase();
        const device = String(item.deviceName || "").toLowerCase();
        const model = String(item.deviceModel || "").toLowerCase();
        return (
          id.includes(kw) ||
          customer.includes(kw) ||
          device.includes(kw) ||
          model.includes(kw)
        );
      });
    },
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    statusLabel,
    urgencyLabel,
    progressLabel,
    statusTagStyle,
    tableCellStyle() {
      return {
        background: "transparent",
        color: "#e8f4ff",
        textAlign: "center",
        padding: "5px 0",
        borderBottom: "1px solid rgba(0, 180, 220, 0.15)",
        cursor: "pointer",
        fontSize: "12px",
      };
    },
    rowClassName({ rowIndex }) {
      return rowIndex % 2 === 0 ? "row-even" : "row-odd";
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.$emit("search", String(this.searchText || "").trim());
      }, 300);
    },
    handleEnter() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.$emit("search", String(this.searchText || "").trim());
    },
    rowClick(row) {
      this.$emit("row-click", row);
    },
  },
};
</script>

<style scoped lang="scss">
@import "../css/screen-theme.scss";

.workorder-table-container {
  @include screen-tech-frame;
  @include screen-tech-corners-bottom;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 8px 12px 10px;
  box-sizing: border-box;
}

.flex-container {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0 0 8px 0;
  flex-shrink: 0;
  padding: 2px 0 6px;
  box-sizing: border-box;
  background: $screen-header-bar;
}

.title-mark {
  @include screen-title-mark;
  margin: 0;
}

.name {
  flex: 0 0 auto;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: $screen-text;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(0, 229, 255, 0.55);
  white-space: nowrap;
}

.search-input {
  flex: 0 0 auto;
  width: 200px;
  margin: 0;
  border: 1px solid rgba(0, 229, 255, 0.4);
  outline: none;
  padding: 4px 10px;
  background: rgba(0, 30, 60, 0.55);
  color: #e8f4ff;
  font-size: 12px;
  clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%);

  &::placeholder {
    color: #5a8aad;
  }
  &:focus {
    border-color: #00e5ff;
    box-shadow: 0 0 10px rgba(0, 229, 255, 0.35);
  }
}

.status-select {
  flex: 0 0 auto;
  width: 120px;
  margin: 0;
  border: 1px solid rgba(0, 229, 255, 0.4);
  outline: none;
  padding: 4px 8px;
  background: rgba(0, 30, 60, 0.55);
  color: #e8f4ff;
  font-size: 12px;
  cursor: pointer;

  option {
    background: #061830;
    color: #e8f4ff;
  }

  &:focus {
    border-color: #00e5ff;
    box-shadow: 0 0 10px rgba(0, 229, 255, 0.35);
  }
}

.el-table-overview {
  width: 100%;
  flex: 1;
  min-height: 0;
  background: transparent !important;
}

::v-deep .el-table {
  background: transparent;
  color: #e8f4ff;

  &::before {
    background: transparent;
  }

  tr,
  th.el-table__cell {
    background: transparent;
  }

  .el-table__header-wrapper th.el-table__cell {
    background: rgba(0, 100, 140, 0.75) !important;
  }

  .row-even td.el-table__cell {
    background: rgba(0, 60, 100, 0.18) !important;
  }
  .row-odd td.el-table__cell {
    background: transparent !important;
  }

  .el-table__body tr:hover > td.el-table__cell {
    background: rgba(0, 180, 220, 0.18) !important;
  }

  td.el-table__cell,
  th.el-table__cell {
    border-color: rgba(0, 180, 220, 0.15) !important;
  }

  .el-table__empty-text {
    color: $screen-text-dim;
  }

  td.col-wrap-text .cell {
    white-space: normal !important;
    word-break: break-all;
    line-height: 1.4;
    text-overflow: clip !important;
    text-align: center !important;
  }

  .wrap-text-cell {
    display: inline-block;
    width: 100%;
    white-space: normal;
    word-break: break-all;
    line-height: 1.4;
    text-align: center;
  }
}

::v-deep .el-loading-mask {
  background-color: rgba(4, 11, 26, 0.55);
}
</style>
